import React, { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { installNotifyFeedbackUnlock, playNotifyFeedback } from "../utils/notifyFeedback";

const MANUAL_SUPPORT_FETCH_STARTED_KEY = "bloomberg_manual_support_fetch_started_v1";
const MANUAL_SUPPORT_AUTO_REFRESH_KEY = "bloomberg_manual_support_auto_refresh_v1";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const TOKEN_KEY = "ct_login_token";
const NEXO_API = window.location.hostname === "localhost"
  ? "http://localhost:5000/api/nexo"
  : "/api/nexo";

const ALERT_IDS_KEY = "bloomberg_global_alert_ids_v1";

const C = {
  gold: "#f7c948",
  dimGold: "#9f7a24",
  red: "#ef4444",
  maroon: "#4a0712",
  line: "rgba(247,201,72,0.26)",
};

function loadAlertIds() {
  try {
    return new Set(JSON.parse(localStorage.getItem(ALERT_IDS_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function saveAlertIds(ids) {
  localStorage.setItem(ALERT_IDS_KEY, JSON.stringify([...ids]));
}

function money(value) {
  return Number(value || 0).toFixed(2);
}

function getManualSupportMeta(key, row) {
  const parts = String(key || "").split("-");

  return {
    chainKey: row?.chainKey || parts[0],
    side: row?.side || parts[1],
    strike: Number(row?.strike || parts[2] || 0),
  };
}

function getScannerUrl(alert) {
  const tab =
    alert.chainKey === "sensex" || alert.chainKey === "bankex"
      ? "sensex-support-scanner"
      : "support-scanner";

  return `/bloomberg-terminal?tab=${tab}&chainKey=${alert.chainKey}&side=${alert.side}&strike=${alert.strike}`;
}

async function getBloombergData() {
  const token = localStorage.getItem(TOKEN_KEY);
  let settings = {};

  if (token) {
    const settingsRes = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const settingsData = await settingsRes.json().catch(() => ({}));
    settings = settingsData.settings || {};
  }

  const res = await fetch(`${API_BASE}/api/bloomberg/snapshot?t=${Date.now()}`, {
    cache: "no-store",
  });
  const snapshot = await res.json().catch(() => ({}));

  return {
    settings,
    snapshot,
  };
}

async function ensureNexoAlertsGroup(token) {
  const groupsRes = await fetch(`${NEXO_API}/groups`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const groupsData = await groupsRes.json().catch(() => ({}));
  const groups = groupsData.groups || [];

  const existing = groups.find(group =>
    String(group.name || "").trim().toLowerCase() === "alerts"
  );

  if (existing) return existing;

  const createRes = await fetch(`${NEXO_API}/groups`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      name: "Alerts",
      description: "Bloomberg support scanner alerts",
    }),
  });

  const createData = await createRes.json().catch(() => ({}));

  return createData.group || createData.data || null;
}

async function sendAlertToNexo(alert) {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;

  const group = await ensureNexoAlertsGroup(token);
  if (!group?.id) return;

  await fetch(`${NEXO_API}/groups/${group.id}/signals`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      side: alert.side === "PE" ? "SELL" : "BUY",
      symbol: `ALERT ${alert.symbol}`,
      entry: String(alert.price),
      stopLoss: String(alert.stopLoss || ""),
      target: alert.type,
      note: alert.message,
    }),
  });
}

export default function GlobalBloombergAlertCenter() {
  const navigate = useNavigate();
  const notifiedRef = useRef(loadAlertIds());
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    return installNotifyFeedbackUnlock();
  }, []);

  useEffect(() => {
    let alive = true;

    const checkAlerts = async () => {
     if (
        localStorage.getItem(MANUAL_SUPPORT_FETCH_STARTED_KEY) !== "1" ||
        localStorage.getItem(MANUAL_SUPPORT_AUTO_REFRESH_KEY) !== "1"
    ) {
     return;
    }
      const { settings, snapshot } = await getBloombergData();
      const ranges = settings.bloombergManualSupportRanges || {};

      Object.entries(ranges).forEach(([key, range]) => {
        if (range?.notifyEnabled !== true) return;

        const meta = getManualSupportMeta(key, range);
        const chain = snapshot?.optionChains?.[meta.chainKey];
        const rows = meta.side === "CE" ? chain?.calls || [] : chain?.puts || [];
        const optionRow = rows.find(row => Number(row.strike || 0) === meta.strike);

        const price = Number(optionRow?.ltp || range.entry || 0);
        const supportLow = Number(range.supportLow || 0);
        const supportHigh = Number(range.supportHigh || 0);
        const stopLoss = Number(range.stopLossPrice || 0);

        if (!price || !supportLow || !supportHigh) return;

        const symbol = `${String(meta.chainKey || "").toUpperCase()} ${meta.side} ${meta.strike}`;
        const baseId = `${key}-${supportLow}-${supportHigh}-${stopLoss}`;

        const fireOnce = (type, message) => {
          const id = `${baseId}-${type}`;
          if (notifiedRef.current.has(id)) return;

          notifiedRef.current.add(id);
          saveAlertIds(notifiedRef.current);

          const alert = {
            id,
            type,
            symbol,
            chainKey: meta.chainKey,
            side: meta.side,
            strike: meta.strike,
            price,
            stopLoss,
            message,
            createdAt: new Date().toISOString(),
          };

        if (alive) setPopup(alert);
        playNotifyFeedback();
        sendAlertToNexo(alert).catch(() => {});
        };

        if (price <= supportHigh) {
          fireOnce(
            "support-zone-1",
            `${symbol} touched Support Zone 1. Price Rs.${money(price)} reached Rs.${money(supportHigh)}.`
          );
        }

        if (price <= supportLow) {
          fireOnce(
            "support-zone-2",
            `${symbol} touched Support Zone 2. Price Rs.${money(price)} reached Rs.${money(supportLow)}.`
          );
        }

        if (stopLoss && price <= stopLoss) {
          fireOnce(
            "stoploss",
            `${symbol} hit StopLoss. Price Rs.${money(price)} reached SL Rs.${money(stopLoss)}.`
          );
        }
      });
    };

    checkAlerts().catch(() => {});
    const timer = setInterval(() => {
      checkAlerts().catch(() => {});
    }, 1000);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  if (!popup) return null;

  return (
    <div
      className="fixed right-5 top-5 z-[9999] max-w-[460px] rounded-lg border p-4 shadow-xl"
      style={{
        background: "#1f0308",
        borderColor: C.red,
        color: C.gold,
        boxShadow: "0 0 22px rgba(239,68,68,0.42)",
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <button
          type="button"
          onClick={() => navigate(getScannerUrl(popup))}
          className="flex-1 text-left"
          style={{ background: "transparent", border: "none", boxShadow: "none" }}
        >
          <div className="flex items-center gap-2 font-bold tracking-[0.18em]">
            <Bell size={18} /> ALERT
          </div>
          <div className="mt-2 text-sm">{popup.message}</div>
          <div className="mt-2 text-xs" style={{ color: C.dimGold }}>
            Click to open strike in Support Scanner
          </div>
        </button>

        <button
          type="button"
          onClick={() => setPopup(null)}
          className="rounded px-2 py-1 text-xs font-bold"
          style={{ background: C.maroon, color: "#fecaca" }}
        >
          X
        </button>
      </div>

      <button
        type="button"
        onClick={() => navigate(`/nexo?chat=alerts&alertId=${popup.id}`)}
        className="mt-3 w-full rounded px-3 py-2 text-sm font-bold"
        style={{ background: "linear-gradient(180deg, #064e3b, #022c22)", color: "#d1fae5" }}
      >
        Open Alerts Chat
      </button>
    </div>
  );
}